/**
 * Tipos para el sistema de i18n
 */

export type Locale = "es" | "en" | "fr" | "pt";

export const DEFAULT_LOCALE: Locale = "es";

export const SUPPORTED_LOCALES: readonly Locale[] = ["es", "en", "fr", "pt"] as const;

/**
 * Textos del sitio público de marketing (Next.js).
 */
export interface MarketingTranslationKeys {
  meta: {
    defaultTitle: string;
    defaultDescription: string;
    featuresTitle: string;
    featuresDescription: string;
    methodologyTitle: string;
    methodologyDescription: string;
    legalTitle: string;
    legalDescription: string;
    privacyTitle: string;
    privacyDescription: string;
    termsTitle: string;
    termsDescription: string;
    pricingTitle: string;
    pricingDescription: string;
  };
  nav: {
    ariaMain: string;
    brand: string;
    home: string;
    features: string;
    methodology: string;
    legal: string;
    pricing: string;
    register: string;
    login: string;
    openMenu: string;
    closeMenu: string;
  };
  footer: {
    tagline: string;
    platformTitle: string;
    legalTitle: string;
    supportTitle: string;
    privacy: string;
    terms: string;
    security: string;
    contact: string;
    copyright: string;
  };
  images: {
    landingHeroAlt: string;
    landingVizAlt: string;
    featuresHeroAlt: string;
    featuresProrateAlt: string;
    featuresAiAlt: string;
    legalMapAlt: string;
    methodologyHeroAlt: string;
    methodologyOfficeAlt: string;
  };
  home: {
    heroEyebrow: string;
    heroTitleLine1: string;
    heroTitleLine2: string;
    heroDescription: string;
    ctaPrimary: string;
    ctaSecondary: string;
    cardSurplusLabel: string;
    cardSurplusValue: string;
    highlightsTitle: string;
    highlight1Label: string;
    highlight1Value: string;
    highlight2Label: string;
    highlight2Value: string;
    highlight3Label: string;
    highlight3Value: string;
    highlight4Label: string;
    highlight4Value: string;
    featuresEyebrow: string;
    featuresTitle: string;
    featuresSubtitle: string;
    featureProrateTitle: string;
    featureProrateDesc: string;
    featureOcrTitle: string;
    featureOcrDesc: string;
    featureVaultTitle: string;
    featureVaultDesc: string;
    visualizeTitle: string;
    visualizeSubtitle: string;
    bar1Label: string;
    bar1Value: string;
    bar2Label: string;
    bar2Value: string;
    ctaBottomTitle: string;
    ctaBottomSubtitle: string;
    ctaBottomButton: string;
  };
  featuresPage: {
    heroEyebrow: string;
    heroTitle: string;
    heroTitleItalic: string;
    heroDescription: string;
    f01Label: string;
    f01Title: string;
    f01Body: string;
    f01aTitle: string;
    f01aBody: string;
    f01bTitle: string;
    f01bBody: string;
    profileLabel: string;
    demoRowALabel: string;
    demoRowAPct: string;
    demoRowBLabel: string;
    demoRowBPct: string;
    demoRowCLabel: string;
    demoRowCPct: string;
    f02Label: string;
    f02Title: string;
    f02Body: string;
    f02Quote: string;
    f02QuoteAttribution: string;
    overlayComplete: string;
    overlayCategoryLine: string;
    f03Eyebrow: string;
    f03Title: string;
    f03Subtitle: string;
    vaultB1Title: string;
    vaultB1Body: string;
    vaultChip1: string;
    vaultChip2: string;
    vaultB2Title: string;
    vaultB2Body: string;
    vaultB3Title: string;
    vaultB3Body: string;
    vaultB4Title: string;
    vaultB4Body: string;
    ctaTitle: string;
    ctaSubtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
  };
  methodologyPage: {
    heroEyebrow: string;
    heroTitle: string;
    heroTitleAccent: string;
    heroDescription: string;
    precisionTitle: string;
    precisionBody: string;
    accuracyLabel: string;
    accuracyValue: string;
    stabilityTitle: string;
    stabilityBody: string;
    growthTitle: string;
    growthSubtitle: string;
    riskTitle: string;
    riskSubtitle: string;
    narrativeEyebrow: string;
    narrativeQuote: string;
    narrativeManifesto: string;
    pillar1Num: string;
    pillar1Title: string;
    pillar1Body: string;
    pillar2Num: string;
    pillar2Title: string;
    pillar2Body: string;
    pillar3Num: string;
    pillar3Title: string;
    pillar3Body: string;
    breakTitle: string;
    breakCta: string;
  };
  legalPage: {
    heroEyebrow: string;
    heroTitle: string;
    heroTitleAccent: string;
    heroDescription: string;
    /** Identificación breve del responsable (página legal). */
    controllerName: string;
    controllerTaxNote: string;
    controllerPresence: string;
    privacyTitle: string;
    privacyLead: string;
    privacyBullet1: string;
    privacyBullet2: string;
    privacyBullet3: string;
    privacyReadMore: string;
    termsSectionLabel: string;
    termsTitle: string;
    termsBody: string;
    termsUpdatedLabel: string;
    termsUpdatedValue: string;
    termsCta: string;
    securityTitle: string;
    securityBody: string;
    securityChip1: string;
    securityChip2: string;
    contactTitle: string;
    contactLead: string;
    contactMapTitle: string;
    contactMapBody: string;
    formName: string;
    formNamePlaceholder: string;
    formEmail: string;
    formEmailPlaceholder: string;
    formMessage: string;
    formMessagePlaceholder: string;
    formSubmit: string;
    formSubmitting: string;
    formSuccess: string;
    formErrorGeneric: string;
    formErrorValidation: string;
    formErrorConfig: string;
  };
  /** Política de privacidad (página dedicada, tiendas de aplicaciones). */
  privacyPage: {
    documentEyebrow: string;
    documentTitle: string;
    lastUpdatedLabel: string;
    lastUpdatedValue: string;
    intro: string;
    sectionControllerTitle: string;
    sectionControllerBody: string;
    sectionDataTitle: string;
    sectionDataIntro: string;
    sectionDataBullet1: string;
    sectionDataBullet2: string;
    sectionDataBullet3: string;
    sectionDataBullet4: string;
    sectionDataBullet5: string;
    sectionPurposeTitle: string;
    sectionPurposeBody: string;
    sectionLegalBasisTitle: string;
    sectionLegalBasisBody: string;
    sectionRetentionTitle: string;
    sectionRetentionBody: string;
    sectionThirdPartiesTitle: string;
    sectionThirdPartiesBody: string;
    sectionRightsTitle: string;
    sectionRightsIntro: string;
    sectionRightsBullet1: string;
    sectionRightsBullet2: string;
    sectionRightsBullet3: string;
    sectionRightsBullet4: string;
    sectionRightsBullet5: string;
    sectionMinorsTitle: string;
    sectionMinorsBody: string;
    sectionChangesTitle: string;
    sectionChangesBody: string;
    sectionContactTitle: string;
    sectionContactBody: string;
    /** Dirección publicada en la política (mailto y texto visible). */
    contactEmail: string;
  };
  /** Condiciones generales de uso (primera versión; revisión legal recomendada). */
  termsPage: {
    documentEyebrow: string;
    documentTitle: string;
    lastUpdatedLabel: string;
    lastUpdatedValue: string;
    intro: string;
    sectionPartyTitle: string;
    sectionPartyBody: string;
    sectionServiceTitle: string;
    sectionServiceBody: string;
    sectionAccountTitle: string;
    sectionAccountBody: string;
    sectionAcceptableUseTitle: string;
    sectionAcceptableUseIntro: string;
    sectionAcceptableUseBullet1: string;
    sectionAcceptableUseBullet2: string;
    sectionAcceptableUseBullet3: string;
    sectionAcceptableUseBullet4: string;
    sectionContentTitle: string;
    sectionContentBody: string;
    sectionFeesTitle: string;
    sectionFeesBody: string;
    sectionIpTitle: string;
    sectionIpBody: string;
    sectionDisclaimerTitle: string;
    sectionDisclaimerBody: string;
    sectionLiabilityTitle: string;
    sectionLiabilityBody: string;
    sectionIndemnityTitle: string;
    sectionIndemnityBody: string;
    sectionTerminationTitle: string;
    sectionTerminationBody: string;
    sectionLawTitle: string;
    sectionLawBody: string;
    sectionChangesTitle: string;
    sectionChangesBody: string;
    sectionContactTitle: string;
    sectionContactBody: string;
    contactEmail: string;
  };
  pricingPage: {
    heroEyebrow: string;
    heroTitle: string;
    heroSubtitle: string;
    leadTitle: string;
    leadBody: string;
    noPaymentNote: string;
    priceNote: string;
    plan1Name: string;
    plan1Tagline: string;
    plan1Price: string;
    plan1Period: string;
    plan1RecurringCaption: string;
    plan1F1: string;
    plan1F2: string;
    plan1F3: string;
    plan2Name: string;
    plan2Tagline: string;
    plan2Price: string;
    plan2Period: string;
    plan2RecurringCaption: string;
    plan2Popular: string;
    plan2F1: string;
    plan2F2: string;
    plan2F3: string;
    plan3Name: string;
    plan3Tagline: string;
    plan3Price: string;
    plan3Period: string;
    plan3RecurringCaption: string;
    plan3F1: string;
    plan3F2: string;
    plan3F3: string;
    cta: string;
    includesLabel: string;
  };
}

export interface TranslationKeys {
  // Common
  common: {
    loading: string;
    error: string;
    success: string;
    cancel: string;
    confirm: string;
    save: string;
    delete: string;
    edit: string;
    create: string;
    update: string;
    search: string;
    filter: string;
    actions: string;
    back: string;
    next: string;
    previous: string;
    close: string;
    description: string;
    locale: {
      label: string;
      hint: string;
      es: string;
      en: string;
      fr: string;
      pt: string;
    };
    theme: {
      label: string;
      light: string;
      dark: string;
      system: string;
    };
  };
  
  // Auth
  auth: {
    login: string;
    logout: string;
    register: string;
    email: string;
    password: string;
    forgotPassword: string;
    resetPassword: string;
    rememberMe: string;
    welcome: string;
    welcomeBack: string;
  };
  
  // Email
  email: {
    welcome: {
      subject: string;
      title: string;
      content: string;
      buttonText: string;
    };
    verification: {
      subject: string;
      title: string;
      content: string;
      buttonText: string;
      expiresIn: string;
    };
    passwordReset: {
      subject: string;
      title: string;
      content: string;
      buttonText: string;
      expiresIn: string;
      ignore: string;
    };
    houseInvitation: {
      subject: string;
      title: string;
      greeting: string;
      content: string;
      buttonRegister: string;
      loginHint: string;
      loginCta: string;
      expiresIn: string;
      footer: string;
    };
  };
  
  // Errors
  errors: {
    generic: string;
    notFound: string;
    unauthorized: string;
    forbidden: string;
    validation: string;
    serverError: string;
    networkError: string;
  };
  
  // Validation
  validation: {
    required: string;
    email: string;
    minLength: string;
    maxLength: string;
    passwordMismatch: string;
  };

  /**
   * Dashboard web (Vite): shell, navegación y placeholders de fase 0+.
   * Evita colisión con `api.errors.dashboard` (errores HTTP del resumen).
   */
  dashboardUi: {
    shell: {
      brandTitle: string;
      brandTagline: string;
      /** Eslogan editorial (p. ej. auth / onboarding), color secundario legible en UI. */
      brandSlogan: string;
      addExpense: string;
      searchPlaceholder: string;
      userStubName: string;
      userStubRole: string;
      notificationsComingSoon: string;
      noActiveHouse: string;
      openingBalanceLine: string;
    };
    nav: {
      mainNavigation: string;
      dashboard: string;
      journal: string;
      search: string;
      budget: string;
      vault: string;
      scan: string;
      financings: string;
      /** Finanzas / trazabilidad por hogar (gráficas e insights). */
      analytics: string;
      houses: string;
      profile: string;
      /** Cuenta unificada (avatar, idioma, nombre, más acciones). */
      accountData: string;
      settings: string;
      support: string;
    };
    placeholder: {
      hint: string;
    };
    pages: {
      home: string;
      login: string;
      register: string;
      forgotPassword: string;
      resetPassword: string;
      settingsPassword: string;
      /** Passkeys WebAuthn (ruta /settings/passkeys). */
      settingsPasskeys: string;
      settingsProfile: string;
      /** Datos personales (idioma, nombre, correo). */
      settingsPersonal: string;
      settings: string;
      support: string;
      houses: string;
      houseMembers: string;
      expenses: string;
      financings: string;
      financingNew: string;
      financingDetail: string;
      financingEdit: string;
      expenseNew: string;
      expenseDetail: string;
      expensePaidFeedback: string;
      budget: string;
      vault: string;
      vaultNew: string;
      vaultDetail: string;
      scan: string;
      /** Analítica del hogar (últimos meses). */
      houseAnalytics: string;
      search: string;
      notifications: string;
      /** Ajustes de push, preferencias y dispositivos (ruta /settings/notifications). */
      notificationSettings: string;
      settingsDevices: string;
      onboarding: string;
    };
    searchPage: {
      subtitle: string;
      backHome: string;
      noActiveHouse: string;
      minCharsHint: string;
      emptyPrompt: string;
      resultsFor: string;
      openJournalWithQuery: string;
      noResults: string;
      sectionExpenses: string;
      sectionVault: string;
    };
    states: {
      loadingHint: string;
      emptyTitle: string;
      emptyHint: string;
      errorTitle: string;
      errorHint: string;
      retry: string;
    };
    mobile: {
      openMenu: string;
      homeLink: string;
    };
    analytics: {
      openFromHome: string;
      openFromBudget: string;
      backToBudget: string;
      missingHouse: string;
      subtitle: string;
      empty: string;
      chartTitle: string;
      chartPaid: string;
      chartPlanned: string;
      chartAvgLine: string;
      peakMonthTitle: string;
      peakMonthBody: string;
      peakMonthNone: string;
      avgSpendTitle: string;
      avgSpendHint: string;
      topCategoriesTitle: string;
      otherCategories: string;
      overspendLeaderTitle: string;
      overspendLeaderBody: string;
      reviewTitle: string;
      reviewHint: string;
      reviewEmpty: string;
      reduceTitle: string;
      reduceHint: string;
      reduceEmpty: string;
    };
    onboarding: {
      pageTitle: string;
      progress: string;
      stepWelcomeTitle: string;
      stepWelcomeBody: string;
      stepHouseTitle: string;
      stepHouseBody: string;
      stepHouseHasHouseTitle: string;
      stepHouseHasHouseBody: string;
      stepMembersTitle: string;
      stepMembersBody: string;
      inviteEmailLabel: string;
      inviteEmailPlaceholder: string;
      inviteSend: string;
      inviteSending: string;
      inviteSent: string;
      inviteSkipHint: string;
      stepBudgetTitle: string;
      stepBudgetBody: string;
      budgetAmountLabel: string;
      budgetSkipHint: string;
      stepDoneTitle: string;
      stepDoneBody: string;
      startTour: string;
      goHome: string;
      skipAll: string;
      next: string;
      back: string;
      creatingHouse: string;
      savingBudget: string;
      finishing: string;
      houseNameLabel: string;
      houseNamePlaceholder: string;
      invalidAmount: string;
    };
    productTour: {
      sidebarHomeTitle: string;
      sidebarHomeDesc: string;
      navHousesTitle: string;
      navHousesDesc: string;
      navJournalTitle: string;
      navJournalDesc: string;
      navBudgetTitle: string;
      navBudgetDesc: string;
      homeMembersTitle: string;
      homeMembersDesc: string;
      sidebarAddExpenseTitle: string;
      sidebarAddExpenseDesc: string;
      sidebarAddExpenseClosingDesc: string;
      housesCreateTourTitle: string;
      housesCreateTourDesc: string;
      housesEmptyTourTitle: string;
      housesEmptyTourDesc: string;
      housesMembersLinkTourTitle: string;
      housesMembersLinkTourDesc: string;
      membersInviteTourTitle: string;
      membersInviteTourDesc: string;
      membersSplitTourTitle: string;
      membersSplitTourDesc: string;
      budgetHeroTourTitle: string;
      budgetHeroTourDesc: string;
      budgetGoalTourTitle: string;
      budgetGoalTourDesc: string;
      homeQuickActionsTitle: string;
      homeQuickActionsDesc: string;
      settingsReplay: string;
      settingsReplayHint: string;
      fallbackTitle: string;
      fallbackBody: string;
    };
    auth: {
      appName: string;
      loginTitle: string;
      loginSubtitle: string;
      registerTitle: string;
      registerSubtitle: string;
      forgotTitle: string;
      forgotSubtitle: string;
      resetTitle: string;
      resetSubtitle: string;
      name: string;
      namePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      passwordLabel: string;
      passwordPlaceholder: string;
      confirmPasswordLabel: string;
      signInCta: string;
      signUpCta: string;
      sendResetLink: string;
      saveNewPassword: string;
      createAccountLink: string;
      registerPrompt: string;
      alreadyHaveAccount: string;
      backToLogin: string;
      forgotLink: string;
      forgotSentHint: string;
      resetSuccess: string;
      invalidResetToken: string;
      signingIn: string;
      signingUp: string;
      inviteLoginBanner: string;
      /** Separador visual entre email/contraseña y proveedor social. */
      dividerOr: string;
      continueWithGoogle: string;
      continueWithApple: string;
      continueWithPasskey: string;
      signUpWithPasskey: string;
      registerWithPasskeyAfterSignUp: string;
      continuingWithGoogle: string;
      continuingWithApple: string;
      continuingWithPasskey: string;
      /** Texto breve bajo el botón passkey: prioridad / autofill condicional. */
      passkeyPreferredHint: string;
      errors: {
        generic: string;
        invalidEmailOrPassword: string;
        userAlreadyExists: string;
        passwordTooShort: string;
        passwordTooLong: string;
        invalidEmail: string;
        invalidToken: string;
        resetDisabled: string;
        invalidOrigin: string;
        network: string;
        emailNotVerified: string;
        invalidPassword: string;
        credentialAccountNotFound: string;
        /** Google (u otro social) no está configurado en el servidor. */
        providerNotConfigured: string;
      };
    };
    houses: {
      pageTitle: string;
      createTitle: string;
      nameLabel: string;
      namePlaceholder: string;
      createCta: string;
      creating: string;
      yourRole: string;
      membersLink: string;
      setActive: string;
      emptyTitle: string;
      emptyHint: string;
      cardSummaryPeriod: string;
      cardPlannedShort: string;
      cardSpentShort: string;
      cardUsageShort: string;
      cardStatusWithin: string;
      cardStatusTight: string;
      cardStatusOver: string;
      cardStatusNeutral: string;
      cardSummaryError: string;
      openingBalanceLabel: string;
      openingBalanceEdit: string;
      openingBalanceSave: string;
      openingBalanceCancel: string;
      openingBalanceSaving: string;
      openingBalanceAmountLabel: string;
      openingBalanceHint: string;
      openingBalanceReadOnlyHint: string;
      openingBalanceInvalidAmount: string;
      openingBalanceLoadError: string;
      financialProfileSectionTitle: string;
      financialProfileSubtitle: string;
      financialProfileEdit: string;
      financialProfileFinancingsLink: string;
      currencyLabel: string;
      currencyPlaceholder: string;
      monthlyCapLabel: string;
      monthlyCapPlaceholder: string;
      monthlyCapHint: string;
      monthlyCapNone: string;
      financialProfileReadOnlyHint: string;
      financialProfileInvalidCurrency: string;
      monthlyCapInvalidAmount: string;
      financialProfileSaveSuccess: string;
      financialProfileLoadError: string;
      setActiveSuccess: string;
      editNameCta: string;
      editNameSave: string;
      editNameCancel: string;
      deleteCta: string;
      deleteConfirm: string;
      updateNameSuccess: string;
      updateNameError: string;
      deleteSuccess: string;
      deleteError: string;
    };
    members: {
      pageTitle: string;
      roleLabel: string;
      roleAdmin: string;
      roleMember: string;
      removeCta: string;
      removeConfirm: string;
      columnUser: string;
      columnRole: string;
      columnSplit: string;
      backToHouses: string;
      inviteSectionTitle: string;
      inviteSectionAdminOnly: string;
      inviteHelp: string;
      inviteEmailLabel: string;
      inviteEmailPlaceholder: string;
      inviteSendCta: string;
      inviteSending: string;
      inviteSent: string;
      pendingInvitesTitle: string;
      pendingInviteExpires: string;
      pendingInviteExpiredBadge: string;
      revokeInviteCta: string;
      revokeInviteConfirm: string;
      registerInviteHeadline: string;
      registerInviteSubline: string;
      emailLockedForInvite: string;
      inviteAcceptWrongEmail: string;
      inviteAcceptedJoined: string;
      inviteAcceptedAlreadyMember: string;
      removeOwnerNotAllowed: string;
      removeMemberSuccess: string;
      changeRoleSaved: string;
      roleOwner: string;
      splitSectionTitle: string;
      splitSectionHelp: string;
      splitBudgetEyebrow: string;
      splitNoBudgetHint: string;
      splitOpenBudgetLink: string;
      splitYouBadge: string;
      splitTotalLine: string;
      splitTotalValid: string;
      splitRemainderHint: string;
      splitSaveCta: string;
      splitSaving: string;
      splitSaveSuccess: string;
      splitReadOnlyHint: string;
    };
    financings: {
      pageTitle: string;
      newCta: string;
      emptyTitle: string;
      emptyHint: string;
      stepDataTitle: string;
      stepReviewTitle: string;
      titleLabel: string;
      lenderLabel: string;
      installmentLabel: string;
      contractStartLabel: string;
      endsOnLabel: string;
      notesLabel: string;
      docsHint: string;
      runAiLabel: string;
      runAiHint: string;
      generateRecurringLabel: string;
      generateRecurringHint: string;
      reviewCutoff: string;
      reviewFirstPayment: string;
      reviewFutureCount: string;
      reviewPreviewDates: string;
      reviewWillSeries: string;
      reviewWillAi: string;
      reviewNoSeries: string;
      backCta: string;
      continueCta: string;
      confirmCta: string;
      saving: string;
      aiDisclaimer: string;
      aiSkipped: string;
      aiPending: string;
      aiProcessing: string;
      aiCompleted: string;
      aiFailed: string;
      viewSeriesCta: string;
      listInstallment: string;
      listTermProgress: string;
      listContractEndedBadge: string;
      listInstallmentsSummary: string;
      listEstimatedRemaining: string;
      listNoInstallmentSchedule: string;
      categoryLabel: string;
      categoryNone: string;
      openDocument: string;
      summaryTitle: string;
      keyTermsTitle: string;
      editCta: string;
      deleteCta: string;
      deleteConfirm: string;
      saveCta: string;
      saveSuccess: string;
    };
    home: {
      periodLabel: string;
      spentMonth: string;
      /** Etiqueta de la tarjeta destacada: total = común + personal a aportar este mes. */
      totalToContributeLabel: string;
      plannedBudget: string;
      budgetUsage: string;
      openingBalance: string;
      upcomingTitle: string;
      upcomingEmpty: string;
      ledgerRecentEmpty: string;
      quickActions: string;
      actionExpense: string;
      actionScan: string;
      actionMembers: string;
      insightsStub: string;
      heroOpeningLabel: string;
      heroProjectedBalanceLabel: string;
      heroProjectedBalanceHint: string;
      heroStatSpentLabel: string;
      heroStatForecastExpensesLabel: string;
      heroStatPlannedLabel: string;
      insightBadge: string;
      insightTitleWithin: string;
      insightTitleTight: string;
      insightTitleOver: string;
      insightTitleNeutral: string;
      insightBodyWithin: string;
      insightBodyTight: string;
      insightBodyOver: string;
      insightBodyNeutral: string;
      insightCta: string;
      ledgerEyebrow: string;
      ledgerTitle: string;
      ledgerViewAll: string;
      budgetProgressSubline: string;
      gridSpentHint: string;
      gridForecastHint: string;
      forecastExpensesMonth: string;
      gridBudgetHint: string;
      gridUsageHint: string;
      gridSpentSharedLine: string;
      gridSpentPersonalLine: string;
      gridBudgetMonthlyLabel: string;
      ledgerRowBadge: string;
      moneySnapshotTitle: string;
      moneySnapshotOpening: string;
      moneySnapshotNeedMonth: string;
      moneySnapshotRetained: string;
      moneySnapshotAvailableAfterRetained: string;
      moneySnapshotProjectedBalance: string;
      budgetSetupUrgentTitle: string;
      budgetSetupUrgentBody: string;
      budgetSetupUrgentCta: string;
      budgetSetupCapOnlyTitle: string;
      budgetSetupCapOnlyBody: string;
      budgetSetupCapOnlyCta: string;
    };
    expenses: {
      newCta: string;
      paid: string;
      unpaid: string;
      paidBy: string;
      scopeShared: string;
      scopePersonal: string;
      category: string;
      backToJournal: string;
      backToExpenseDetail: string;
      ledgerMonthTotalLabel: string;
      ledgerMonthForecastTotalLabel: string;
      ledgerMemberSharesTitle: string;
      ledgerMemberSharesForecastTitle: string;
      ledgerNoSplitsInMonth: string;
      listForecastVsActual: string;
      actionMarkPaid: string;
      actionSetActualAmount: string;
      markPaidConfirmTitle: string;
      markPaidConfirmBody: string;
      markPaidConfirmCta: string;
      actionPaidZero: string;
      paidZeroConfirmTitle: string;
      paidZeroConfirmBody: string;
      paidZeroConfirmCta: string;
      actualAmountModalTitle: string;
      actualAmountModalHint: string;
      actualAmountLabel: string;
      actualAmountClear: string;
      actualAmountSubmit: string;
      paidFeedbackTitle: string;
      paidFeedbackHero: string;
      paidFeedbackSubline: string;
      paidFeedbackAmountLabel: string;
      paidFeedbackCategoryLabel: string;
      paidFeedbackPaidByLabel: string;
      paidFeedbackDateLabel: string;
      paidFeedbackContribution: string;
      paidFeedbackBackHome: string;
      paidFeedbackViewDetail: string;
      form: {
        titleLabel: string;
        titlePlaceholder: string;
        titleOptionalHint: string;
        amountLabel: string;
        amountHint: string;
        currencyLabel: string;
        dateLabel: string;
        categoryLabel: string;
        categoryNone: string;
        paidByLabel: string;
        paidByDefault: string;
        paidByJointOption: string;
        paidByHint: string;
        paymentMethodLabel: string;
        paymentMethodNone: string;
        paymentMethodCard: string;
        paymentMethodCash: string;
        paymentMethodAccount: string;
        sharedLabel: string;
        sharedHint: string;
        recurringQuestion: string;
        recurringEndsLabel: string;
        /** Serie sin prorrateo: la fecha de fin es opcional. */
        recurringEndsOptionalHelp: string;
        /** Prorrateo mensual: hace falta fecha de fin. */
        recurringEndsRequiredWhenProratedHelp: string;
        /** Prorrateo con frecuencia > 1 mes: total por periodo, sin fin obligatorio. */
        recurringEndsProrateOpenPeriodHelp: string;
        recurringFrequencyLabel: string;
        frequencyMonthly: string;
        frequencyBimonthly: string;
        frequencyQuarterly: string;
        frequencySemiannual: string;
        frequencyAnnual: string;
        proratedLabel: string;
        proratedHint: string;
        recurringSummary: string;
        /** Serie activa sin fecha de fin: horizonte inicial de materialización. */
        recurringOpenEndedSummary: string;
        /** Nota en vista previa: prorrateo por periodo sin fin. */
        recurringProratedOpenPeriodPreviewNote: string;
        submitRecurring: string;
        notesLabel: string;
        notesPlaceholder: string;
        receiptLabel: string;
        receiptHelp: string;
        receiptRemove: string;
        paidNowLabel: string;
        submitCreate: string;
        submitSave: string;
        saving: string;
        cancelEdit: string;
        invalidAmount: string;
        titleOrCategoryRequired: string;
        recurrenceInvalid: string;
        /** Validación: prorrateo marcado pero sin fecha de fin. */
        recurringProratedRequiresEndsOn: string;
        recurringPreviewNeedAmount: string;
        /** Cuando el recurrente es «cargo mensual fijo» y falta importe en el campo de arriba. */
        recurringPreviewNeedAmountMonthlyFixed: string;
        proratedMonthlyAverageHint: string;
        recurrenceKindLegend: string;
        recurrenceKindMonthlyFixed: string;
        recurrenceKindSeries: string;
        seriesSingleChargeHint: string;
        seriesAnnualPresetAction: string;
        amountLabelMonthly: string;
        amountHintMonthlyFixed: string;
        monthlyFixedPatternExplain: string;
        monthlyFixedHorizonNote: string;
        /** Factura bimestral modelada como reserva mensual (mitad del importe). */
        smoothBimonthlyBillHint: string;
        sharedSplitPreviewTitle: string;
        sharedSplitPreviewSubtitle: string;
        sharedSplitUsesHousePercent: string;
        sharedSplitEqualParts: string;
      };
      detail: {
        edit: string;
        markPaid: string;
        markUnpaid: string;
        delete: string;
        deleteConfirm: string;
        paidAtLabel: string;
        notesTitle: string;
        splitsTitle: string;
        splitsSubtitle: string;
        splitShare: string;
        notApplicable: string;
        scopeLabel: string;
        paymentMethod: string;
        receiptLabel: string;
        receiptView: string;
        receiptNotAttached: string;
        receiptReplaceHelp: string;
        seriesLink: string;
        seriesFutureInstallmentLabel: string;
        seriesSave: string;
        seriesLimitSectionTitle: string;
        seriesLimitSectionIntro: string;
        seriesEndsOnLabel: string;
        seriesEndsOnHelp: string;
        seriesApplyEndsOn: string;
        seriesEndNow: string;
        seriesEndNowConfirm: string;
        seriesExtendSectionTitle: string;
        seriesExtendIntroActiveWithEnd: string;
        seriesExtendIntroEnded: string;
        seriesRemoveEndsOn: string;
        seriesRemoveEndsOnConfirm: string;
        seriesReactivateOpenEnded: string;
        seriesReactivateOpenEndedConfirm: string;
        seriesReactivateOnly: string;
        seriesReactivateOnlyConfirm: string;
        seriesExtendIntroEndedNoEnd: string;
        seriesStatusBadgeActive: string;
        seriesStatusBadgePaused: string;
        seriesStatusBadgeEnded: string;
        seriesEndedReadOnly: string;
        seriesInvalidEndDate: string;
        budgetSetupBannerTitle: string;
        budgetSetupBannerBody: string;
        budgetSetupBannerCta: string;
      };
      filters: {
        title: string;
        searchPlaceholder: string;
        scopeLabel: string;
        scopeAll: string;
        scopeShared: string;
        scopePersonal: string;
        categoryAll: string;
        paidAll: string;
        paidYes: string;
        paidNo: string;
        apply: string;
        clear: string;
      };
      /** Entradas y asistentes: gasto puntual / fijo mensual / serie (paridad con app). */
      flows: {
        launcherTitle: string;
        launcherSubtitle: string;
        manualOnlyBanner: string;
        intentQuickTitle: string;
        intentQuickDescription: string;
        intentRecurringTitle: string;
        intentRecurringDescription: string;
        intentSeriesTitle: string;
        intentSeriesDescription: string;
        quickExpandMore: string;
        /** CTA con {{flowLabel}} = título del flujo (p. ej. intentQuickTitle). */
        repeatLastFlowCta: string;
        navNext: string;
        navBack: string;
        navReview: string;
        editStepA11y: string;
        monthlyWizard: {
          step1Title: string;
          step1Help: string;
          step2Title: string;
          step2Help: string;
          step3Title: string;
          step3Help: string;
          step4Title: string;
          step4Help: string;
          step5Title: string;
          step5Help: string;
          reviewTitle: string;
          reviewHelp: string;
        };
        seriesWizard: {
          step1Title: string;
          step1Help: string;
          step2Title: string;
          step2Help: string;
          step3Title: string;
          step3Help: string;
          step4Title: string;
          step4Help: string;
          step5Title: string;
          step5Help: string;
          reviewTitle: string;
          reviewHelp: string;
        };
      };
    };
    budget: {
      heroEyebrow: string;
      heroTitle: string;
      heroSubtitle: string;
      goalLabel: string;
      percentComplete: string;
      reservedLabel: string;
      targetLabel: string;
      categoryGridTitle: string;
      plannedLabel: string;
      spentLabel: string;
      plannedInputHint: string;
      monthNav: string;
      prevMonth: string;
      nextMonth: string;
      saveCta: string;
      saving: string;
      saveSuccess: string;
      copyFromPreviousConfirmTitle: string;
      copyFromPreviousConfirmMessage: string;
      copyFromPreviousCta: string;
      readOnlyHint: string;
      performanceTitle: string;
      performanceSubtitle: string;
      legendBudgeted: string;
      legendSpent: string;
      noCategoriesHint: string;
      addCategoryCard: string;
      addCategoryCardHint: string;
      addCategoryFormTitle: string;
      addCategoryNameLabel: string;
      addCategoryNamePlaceholder: string;
      addCategorySubmit: string;
      addCategoryCancel: string;
      addCategoryCreating: string;
      heroVsPrevUp: string;
      heroVsPrevDown: string;
      heroVsPrevFlat: string;
      proratedTitle: string;
      proratedLinkJournal: string;
      proratedReserveHint: string;
      proratedTotalBadge: string;
      proratedEmpty: string;
      planningSectionTitle: string;
      bannerEyebrow: string;
      bannerTitle: string;
      badgeOverBudget: string;
      badgeUnderBudget: string;
      badgeOnTrack: string;
      badgeNoPlan: string;
      columnBudget: string;
      columnReal: string;
      analysisEmpty: string;
      monthlyCapHint: string;
      moneySnapshotTitle: string;
      moneySnapshotOpening: string;
      moneySnapshotNeedMonth: string;
      moneySnapshotRetained: string;
      moneySnapshotAvailableAfterRetained: string;
      moneySnapshotProjectedBalance: string;
    };
    vault: {
      pageTitle: string;
      pageSubtitle: string;
      emptyHint: string;
      addCta: string;
      nameLabel: string;
      namePlaceholder: string;
      assetTypeLabel: string;
      assetTypePlaceholder: string;
      purchaseDateLabel: string;
      warrantyLabel: string;
      notesLabel: string;
      notesPlaceholder: string;
      notesOptionalHint: string;
      documentKeyLabel: string;
      documentKeyPlaceholder: string;
      saveCta: string;
      saving: string;
      createCta: string;
      backToList: string;
      deleteCta: string;
      deleteConfirm: string;
      readOnlyHint: string;
      cardWarranty: string;
      cardPurchased: string;
      formKicker: string;
      formTitle: string;
      formSubtitle: string;
      priceLabel: string;
      pricePlaceholder: string;
      priceInNotesLine: string;
      documentUploadTitle: string;
      documentUploadHint: string;
      documentUploadSub: string;
      documentUploadedHint: string;
      completionLabel: string;
      saveLegalNote: string;
      filterAll: string;
      filterEmpty: string;
      alertKicker: string;
      alertTitle: string;
      alertDaysLeft: string;
      alertExpired: string;
      alertViewDetails: string;
      statTotalLabel: string;
      statSecuredLabel: string;
      statSecuredLine: string;
      sectionCount: string;
      cardPurchaseDate: string;
      cardExpiryDate: string;
      viewDocument: string;
      registerNewItem: string;
      warrantyBadgeExpired: string;
      warrantyBadgeUrgent: string;
      warrantyBadgeWarning: string;
      warrantyBadgeSafe: string;
      warrantyBadgeNone: string;
      warrantyEndsToday: string;
      categoryPlaceholder: string;
      sectionUncategorized: string;
      categories: {
        appliances: string;
        electronics: string;
        furniture: string;
        vehicles: string;
        other: string;
      };
    };
    scan: {
      overlayTitle: string;
      overlaySubtitle: string;
      pickFile: string;
      uploading: string;
      registering: string;
      statusPending: string;
      statusProcessing: string;
      statusCompleted: string;
      statusFailed: string;
      extractedTitle: string;
      rawPayloadHint: string;
      recentTitle: string;
      linkExpenseHint: string;
      pollHint: string;
      createExpenseFromScan: string;
      fromScanBanner: string;
      extractionErrorFallback: string;
      extractedMerchant: string;
      extractedDate: string;
      extractedTotal: string;
    };
    profile: {
      pageTitle: string;
      accountSettings: string;
      accountSettingsHint: string;
      notifications: string;
      notificationsHint: string;
      devices: string;
      devicesHint: string;
      privacy: string;
      privacyHint: string;
      help: string;
      helpHint: string;
      logout: string;
      logoutHint: string;
      footerNote: string;
      /** Idioma + nombre visible en una sola tarjeta (perfil). */
      profileFormSectionTitle: string;
      nameField: string;
      saveButton: string;
      saving: string;
      savedHint: string;
      clearAvatarButton: string;
      avatarChangeAriaLabel: string;
      avatarUploading: string;
      avatarUploadError: string;
    };
    personalDataPage: {
      subtitle: string;
      emailLabel: string;
      emailHint: string;
      deleteAccountSectionTitle: string;
      deleteAccountSectionHint: string;
      deleteAccountButton: string;
      deleteAccountModalTitle: string;
      deleteAccountModalBody: string;
      deleteAccountModalConfirm: string;
      deleteAccountModalCancel: string;
      deleteAccountError: string;
      deleteAccountPending: string;
    };
    settingsHub: {
      unifiedPageTitle: string;
      unifiedSubtitle: string;
      backToAccount: string;
      cardPersonalDataTitle: string;
      cardPersonalDataHint: string;
      cardPasswordTitle: string;
      cardPasswordHint: string;
      cardPasskeysTitle: string;
      cardPasskeysHint: string;
      cardNotificationsTitle: string;
      cardNotificationsHint: string;
      cardDevicesTitle: string;
      cardDevicesHint: string;
      cardSupportTitle: string;
      cardSupportHint: string;
      cardDeleteAccountTitle: string;
      cardDeleteAccountHint: string;
    };
    settingsPasswordForm: {
      subtitle: string;
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
      submit: string;
      successMessage: string;
      forgotPasswordCta: string;
      noCredentialHint: string;
      changeAgain: string;
    };
    settingsPasskeysPage: {
      subtitle: string;
      disabledHint: string;
      addHint: string;
      addButton: string;
      adding: string;
      loading: string;
      loadError: string;
      empty: string;
      unnamedPasskey: string;
      deviceTypeLabel: string;
      createdLabel: string;
      renameButton: string;
      renameDialogTitle: string;
      renameLabel: string;
      renameSave: string;
      renameEmpty: string;
      deleteButton: string;
      deleteConfirm: string;
      deleting: string;
    };
    supportPage: {
      subtitle: string;
      intro: string;
      forgotTitle: string;
      forgotBody: string;
      forgotCta: string;
    };
    notificationsPage: {
      subtitle: string;
      emptyTitle: string;
      emptyHint: string;
      inboxTitle: string;
      pushSectionTitle: string;
      activatePush: string;
      pushWorking: string;
      permissionDenied: string;
      notSupported: string;
      configMissing: string;
      registerError: string;
      devicesSectionTitle: string;
      noDevices: string;
      platformLabel: string;
      platformWeb: string;
      platformIos: string;
      platformAndroid: string;
      lastSeenLabel: string;
      removeDevice: string;
      prefsSectionTitle: string;
      prefPushMaster: string;
      prefHouseInvites: string;
      prefExpenses: string;
      prefBudget: string;
      prefReminders: string;
      prefScanDocs: string;
      prefDigests: string;
      prefUpcomingCharges: string;
      markAllRead: string;
      markOneRead: string;
      loadMore: string;
      unreadLabel: string;
      viewAction: string;
      removeDeviceConfirm: string;
      devicePushLabel: string;
    };
    pendingInvitationsPage: {
      title: string;
      subtitle: string;
      emptyTitle: string;
      emptyHint: string;
      inviterLine: string;
      expiresLabel: string;
      acceptCta: string;
      accepting: string;
      loadError: string;
    };
    notificationSettingsPage: {
      subtitle: string;
    };
    notificationsInApp: {
      pushDeviceRegisteredTitle: string;
      pushDeviceRegisteredBody: string;
      receiptScanCompletedTitle: string;
      receiptScanCompletedBody: string;
      receiptScanFailedTitle: string;
      receiptScanFailedBody: string;
      houseInvitationReceivedTitle: string;
      houseInvitationReceivedBody: string;
      houseInvitationAcceptedTitle: string;
      houseInvitationAcceptedBody: string;
      budgetThreshold80Title: string;
      budgetThreshold80Body: string;
      budgetThreshold100Title: string;
      budgetThreshold100Body: string;
      budgetMonthStartedTitle: string;
      budgetMonthStartedBody: string;
      budgetMonthNeedsSetupTitle: string;
      budgetMonthNeedsSetupBody: string;
      budgetMonthSeededFromPreviousTitle: string;
      budgetMonthSeededFromPreviousBody: string;
      vaultWarrantyExpiring7dTitle: string;
      vaultWarrantyExpiring7dBody: string;
      upcomingExpenseTomorrowTitle: string;
      upcomingExpenseTomorrowBody: string;
    };
    devicesPage: {
      subtitle: string;
      intro: string;
      thisDevice: string;
      currentBadge: string;
      lastActive: string;
      sessionStarted: string;
      expires: string;
      ip: string;
      browser: string;
      unknownBrowser: string;
      signOutDevice: string;
      signOutDeviceConfirm: string;
      signOutOthers: string;
      signOutOthersConfirm: string;
      loadError: string;
      actionError: string;
      empty: string;
    };
  };

  /** Sitio público de marketing (landing, características, metodología, legal). */
  marketing: MarketingTranslationKeys;

  /**
   * API Nest: errores con `key` estable + `message` resuelto por Accept-Language.
   * Rutas de clave: `api.errors.*` (ver docs/api-error-responses.md).
   */
  api: {
    errors: {
      validationFailed: string;
      unauthorized: string;
      forbidden: string;
      internal: string;
      fallback: string;
      validation: {
        expectedString: string;
        invalidUuid: string;
        expectedInt: string;
        expectedBoolean: string;
        expectedArray: string;
        expectedObject: string;
        invalidEnum: string;
        min: string;
        max: string;
        pattern: string;
        invalidField: string;
      };
      houses: {
        nameRequired: string;
        createFailed: string;
        notFound: string;
        nameEmpty: string;
        userIdRequired: string;
        cannotAssignOwnerViaApi: string;
        userAlreadyMember: string;
        memberNotFound: string;
        atLeastOneOwner: string;
        defaultSplitBpsRange: string;
        cannotRemoveSoleOwner: string;
        financialProfileNotFound: string;
        currencyIso: string;
        invitationEmailInvalid: string;
        invitationNotFound: string;
        invitationTokenInvalid: string;
        invitationEmailMismatch: string;
        invitationEmailFailed: string;
        invitationUserMissing: string;
      };
      access: {
        notMember: string;
        adminRequired: string;
        ownerOnly: string;
      };
      expenses: {
        userNotMember: string;
        notFound: string;
        titleRequired: string;
        amountMinorInvalid: string;
        categoryInvalidForHouse: string;
        categoryInvalid: string;
        currencyIso: string;
        currencyInvalid: string;
        createFailed: string;
        titleEmpty: string;
        splitSumMustBe10000: string;
        splitDuplicateUser: string;
        splitShareBpsInvalid: string;
        recurrenceEndsBeforeStart: string;
        recurrenceNoOccurrences: string;
        proratedRequiresEndsOn: string;
        seriesNotFound: string;
        seriesFixedSplitsRequired: string;
        seriesSplitsOnlyForFixed: string;
        seriesEndsBeforeAnchor: string;
        seriesEndedWithFutureEndsOn: string;
      };
      financings: {
        notFound: string;
        titleRequired: string;
        installmentInvalid: string;
        contractStartInvalid: string;
        endsOnInvalid: string;
        endsBeforeContract: string;
        noFutureInstallments: string;
        aiRequiresDocuments: string;
        documentStorageKeyRequired: string;
        summarizationFailed: string;
        createFailed: string;
      };
      budgets: {
        yearMonthFormat: string;
        plannedAmountInvalid: string;
        categoryNotInHouse: string;
        categoryNotFound: string;
      };
      vault: {
        notFound: string;
        nameRequired: string;
        nameEmpty: string;
        assetTypeInvalid: string;
      };
      receiptScans: {
        notFound: string;
        imageKeyRequired: string;
        statusInvalid: string;
        dailyLimitExceeded: string;
        extractionFailed: string;
        extractionDisabled: string;
        imageTooLarge: string;
        unsupportedMedia: string;
      };
      categories: {
        nameRequired: string;
        notFound: string;
        nameEmpty: string;
      };
      dashboard: {
        yearMonthInvalid: string;
        yearMonthFormat: string;
      };
      notifications: {
        notFound: string;
        deviceNotFound: string;
      };
      storage: {
        fileRequired: string;
        mimeNotAllowed: string;
        fileTooLarge: string;
        keyRequired: string;
        invalidKey: string;
      };
      account: {
        userNotFound: string;
        deletionBlockedReferences: string;
      };
      publicContact: {
        sendFailed: string;
        serviceUnavailable: string;
      };
    };
  };

  /** App móvil (React Native): shell y pantallas de ejemplo del esqueleto. */
  mobile: {
    common: {
      cancel: string;
      confirm: string;
      /** Tour guiado u otros flujos opcionales. */
      skip: string;
      ok: string;
      /** Toasts tras guardar cambios genéricos (p. ej. serie recurrente). */
      changesSaved: string;
      retry: string;
      loadError: string;
      signingOut: string;
      signOutConfirmTitle: string;
      signOutConfirmMessage: string;
      signOutFailedTitle: string;
      signOutFailed: string;
    };
    session: {
      checking: string;
    };
    /** Errores HTTP/red: fallback cuando el cuerpo de la API no trae `message` o falla `fetch`. */
    api: {
      errors: {
        network: string;
        generic: string;
        serverUnavailable: string;
        missingConfig: string;
      };
    };
    tabs: {
      home: string;
      houses: string;
      scan: string;
      budget: string;
      vault: string;
    };
    shell: {
      comingSoon: string;
    };
    home: {
      title: string;
      subtitle: string;
      heroEyebrow: string;
      heroTitle: string;
      heroSubtitle: string;
      heroTrend: string;
      heroRemainingLabel: string;
      openSheet: string;
      openPaginatedDemo: string;
      healthChecking: string;
      healthOk: string;
      healthFail: string;
      insightTitle: string;
      insightBody: string;
      insightBadge: string;
      insightValue: string;
      insightValueGood: string;
      insightValueFair: string;
      insightValueOver: string;
      upcomingCardBadge: string;
      upcomingCardBodyGeneric: string;
      upcomingCardNone: string;
      budgetCardTitle: string;
      budgetCardCompleted: string;
      budgetCardTotal: string;
      /** Bloque "de un vistazo" bajo el hero: personal + común + total a aportar. */
      monthSummaryTitle: string;
      monthSummaryPersonalLabel: string;
      monthSummarySharedLabel: string;
      monthSummaryTotalLabel: string;
      monthSummaryForecastPrefix: string;
      /** Título de la sección de presupuesto de la casa/común (antes card "insight"). */
      houseSectionTitle: string;
      /** Título de la sección de gasto personal (sin tope, solo informativa). */
      personalSectionTitle: string;
      /** Fase 1 — acciones rápidas Stitch (`inicio_panel_de_control`). */
      quickActionsTitle: string;
      quickExpense: string;
      quickScan: string;
      quickMembers: string;
      /** Acción rápida del FAB: ir a financiaciones del hogar activo. */
      quickFinancings: string;
      quickFabA11y: string;
      quickFabCloseA11y: string;
      quickFabLabel: string;
      quickFabCloseLabel: string;
      openJournal: string;
      /** Accesibilidad: icono de diario fijo en la cabecera (acceso sin scroll). */
      openJournalHeaderA11y: string;
      /** Atajo a lista de financiaciones del hogar activo. */
      financingsShortcutTitle: string;
      financingsShortcutSubtitle: string;
      /** Atajo a analítica del hogar (paridad web). */
      analyticsShortcutTitle: string;
      analyticsShortcutSubtitle: string;
      openingBalanceLabel: string;
      openingBalanceMock: string;
      defaultHouseName: string;
      notificationsA11y: string;
      /** Accesibilidad: botón de perfil / avatar en cabecera del inicio. */
      profileA11y: string;
      houseSelectorA11y: string;
      noHouseTitle: string;
      noHouseBody: string;
      chooseHouseTitle: string;
      recentTitle: string;
      /** Enlace "ver todos" bajo el listado recortado de próximos gastos. */
      upcomingViewAll: string;
      goToChangePassword: string;
      budgetSetupUrgentTitle: string;
      budgetSetupUrgentBody: string;
      budgetSetupUrgentCta: string;
      budgetSetupCapOnlyTitle: string;
      budgetSetupCapOnlyBody: string;
      budgetSetupCapOnlyCta: string;
    };
    journal: {
      title: string;
      searchPlaceholder: string;
      fabA11y: string;
      fabLabel: string;
      filterAll: string;
      filterHousehold: string;
      filterPersonal: string;
      filterGroceries: string;
      filterUtilities: string;
      filterInsurance: string;
      groupMonth: string;
      expense1Title: string;
      expense2Title: string;
      expense1Meta: string;
      expense2Meta: string;
      expense1Amount: string;
      expense2Amount: string;
      memberYou: string;
      badgePaid: string;
      badgeUnpaid: string;
      empty: string;
      emptyTitle: string;
      emptyDescription: string;
      emptyFilteredTitle: string;
      emptyCta: string;
    };
    /** Acciones rápidas en filas de gasto (diario, inicio). */
    expenseQuickActions: {
      menuA11y: string;
      markPaidChip: string;
      adjustActualChip: string;
      paidZeroChip: string;
      paidZeroConfirmTitle: string;
      paidZeroConfirmMessage: string;
      paidZeroSuccess: string;
      actualAmountPlaceholder: string;
    };
    /** Pantalla de detalle de gasto (Stitch `detalle_de_gasto_individuall`). */
    expenseDetail: {
      screenTitle: string;
      totalAmountLabel: string;
      dateLabel: string;
      categoryLabel: string;
      scopeLabel: string;
      paymentMethodLabel: string;
      notesLabel: string;
      paidByLabel: string;
      recurringLabel: string;
      proratedLabel: string;
      splitsTitle: string;
      splitPercent: string;
      markAsPaidCta: string;
      markAsUnpaidCta: string;
      markingPaid: string;
      markingUnpaid: string;
      markPaidSuccess: string;
      markUnpaidSuccess: string;
      editCta: string;
      noCategoryLabel: string;
      noNotesLabel: string;
      confirmMarkPaid: string;
      confirmMarkPaidMessage: string;
      confirmMarkUnpaid: string;
      confirmMarkUnpaidMessage: string;
      errorMarkPaid: string;
      errorMarkUnpaid: string;
      cycleLabel: string;
      projectionLabel: string;
      perMonth: string;
      actualCostLabel: string;
      realCostLabel: string;
      updateCostTitle: string;
      updateCostCta: string;
      updateCostError: string;
      updateCostSuccess: string;
      receiptLabel: string;
      receiptAttached: string;
      receiptNotAttached: string;
      receiptViewCta: string;
      receiptAttachCta: string;
      receiptReplaceCta: string;
      receiptSourceTitle: string;
      receiptSourceMessage: string;
      receiptSourceCamera: string;
      receiptSourceGallery: string;
      receiptSourceDocument: string;
      receiptUpdating: string;
      receiptUpdatedSuccess: string;
      receiptUpdateError: string;
      deleteCta: string;
      deleteConfirmTitle: string;
      deleteConfirmMessage: string;
      deleteError: string;
      deleteSuccess: string;
      deleting: string;
      freqMonthly: string;
      freqBimonthly: string;
      freqQuarterly: string;
      freqSemiannual: string;
      freqAnnual: string;
      methodCard: string;
      methodCash: string;
      methodAccount: string;
      budgetSetupBannerTitle: string;
      budgetSetupBannerBody: string;
      budgetSetupBannerCta: string;
    };
    /** Pantalla detalle / gestión serie recurrente (móvil). */
    expenseSeries: {
      screenTitle: string;
    };
    houses: {
      screenTitle: string;
      subtitle: string;
      /** Stitch `mis_casas_y_miembros`: etiqueta superior del hero. */
      managementEyebrow: string;
      /** Stitch: párrafo bajo el título principal. */
      heroDescription: string;
      sampleHouseName: string;
      activeBadge: string;
      membersCta: string;
      budgetHint: string;
      budgetHintLive: string;
      empty: string;
      emptyTitle: string;
      emptyDescription: string;
      setActiveHint: string;
      /** Pantalla crear casa. */
      createScreenTitle: string;
      createSubtitle: string;
      nameLabel: string;
      namePlaceholder: string;
      createCta: string;
      creating: string;
      createSuccess: string;
      createError: string;
      /** Accesos desde tarjeta de casa. */
      financialProfileCta: string;
      financingsCta: string;
      /** Perfil financiero del hogar. */
      financialProfileScreenTitle: string;
      financialProfileSubtitle: string;
      /** Enlace a financiaciones desde perfil financiero. */
      financialProfileFinancingsCta: string;
      openingBalanceLabel: string;
      openingBalancePlaceholder: string;
      currencyLabel: string;
      currencyPlaceholder: string;
      monthlyBudgetLabel: string;
      monthlyBudgetPlaceholder: string;
      saveProfileCta: string;
      savingProfile: string;
      saveProfileSuccess: string;
      saveProfileError: string;
      invalidAmount: string;
      /** Pantalla de miembros (Stitch `mis_casas_y_miembros`). */
      membersScreenTitle: string;
      membersSubtitle: string;
      memberRoleOwner: string;
      memberRoleAdmin: string;
      memberRoleMember: string;
      memberSince: string;
      invitePendingTitle: string;
      inviteExpires: string;
      inviteExpired: string;
      inviteEmpty: string;
      membersEmpty: string;
      inviteCta: string;
      inviteComingSoon: string;
      /** Invitar miembro (formulario real). */
      inviteEmailSection: string;
      inviteRoleLabel: string;
      inviteEmailLabel: string;
      inviteEmailPlaceholder: string;
      inviteSendCta: string;
      inviteSending: string;
      inviteSentSuccess: string;
      inviteError: string;
      revokeInviteCta: string;
      revokeInviteConfirm: string;
      revokeInviteSuccess: string;
      revokeInviteError: string;
      /** Lista de residentes en tarjeta (Stitch). */
      residentsTitle: string;
      manageAllCta: string;
      splitAllocationTitle: string;
      allocatedBudgetLabel: string;
      perMonthSuffix: string;
      adjustSplitCta: string;
      soloMemberHint: string;
      inviteCompactCta: string;
      inviteCardTitle: string;
      inviteCardBody: string;
      inviteCardCta: string;
      proTipTitle: string;
      proTipBody: string;
      proTipLink: string;
      settingsA11y: string;
      useAsActiveCta: string;
      setActiveSuccess: string;
      removeMemberCta: string;
      removeMemberConfirm: string;
      removeMemberSuccess: string;
      removeMemberError: string;
      changeRoleCta: string;
      changeRoleTitle: string;
      roleUpdatedSuccess: string;
      roleUpdateError: string;
      inviteAdminOnly: string;
    };
    budget: {
      screenTitle: string;
      subtitle: string;
      heroMainTitle: string;
      heroMainAccent: string;
      netBalanceLabel: string;
      netBalanceMock: string;
      progressLabel: string;
      progressPercent: string;
      rowCategory: string;
      rowBudgeted: string;
      rowActual: string;
      demoUtilitiesBudgeted: string;
      demoUtilitiesActual: string;
      demoInsuranceBudgeted: string;
      demoInsuranceActual: string;
      emptyRows: string;
      emptyPerformanceTitle: string;
      emptyPerformanceDescription: string;
      proratedTotalLabel: string;
      moneyRequiredLabel: string;
      remanentLabel: string;
      proratedInstallmentType: string;
      proratedInstallmentMonthly: string;
      proratedItemTotal: string;
      proratedMoveHint: string;
      adjustGoalsCta: string;
      fabEditBudget: string;
      fabCreateBudget: string;
      performanceSectionTitle: string;
      performanceSectionSubtitle: string;
      /** Aclara que «Plan» no se calcula desde cargos recurrentes. */
      performanceSectionPlanNote: string;
      filtersCta: string;
      exportPdfCta: string;
      exportPdfComingSoon: string;
      editByCategoryTitle: string;
      editByCategorySubtitle: string;
      createCategoryCta: string;
      createCategoryTitle: string;
      createCategorySubtitle: string;
      categoryCreatedSuccess: string;
      deleteCategoryA11y: string;
      deleteCategoryConfirmTitle: string;
      deleteCategoryConfirmMessage: string;
      deleteCategoryCta: string;
      deleteCategorySuccess: string;
      deleteCategoryError: string;
    };
    analytics: {
      screenTitle: string;
      openFromBudget: string;
      backToBudget: string;
      subtitle: string;
      empty: string;
      chartTitle: string;
      legendPaid: string;
      legendPlanned: string;
      legendAvg: string;
      /** Visible cuando el gráfico tiene scroll horizontal (p. ej. 12 meses). */
      chartScrollHint: string;
      /** Accesibilidad: describe el eje Y del gráfico por mes. */
      chartYAxisA11y: string;
      peakTitle: string;
      peakBody: string;
      peakNone: string;
      avgTitle: string;
      avgHint: string;
      topCategories: string;
      otherCategories: string;
      overspendTitle: string;
      overspendBody: string;
      reviewTitle: string;
      reviewHint: string;
      reviewEmpty: string;
      reduceTitle: string;
      reduceHint: string;
      reduceEmpty: string;
    };
    vault: {
      screenTitle: string;
      subtitle: string;
      searchPlaceholder: string;
      alertTitle: string;
      alertBody: string;
      alertBodyLive: string;
      itemFridge: string;
      itemInsurance: string;
      completionLabel: string;
      empty: string;
      emptyTitle: string;
      emptyDescription: string;
      emptyFilterTitle: string;
      emptyFilterDescription: string;
      /** Detalle del activo (Stitch `b_veda_detalles_del_activo`). */
      detailScreenTitle: string;
      detailBadge: string;
      detailTitle: string;
      detailSubtitle: string;
      detailAssetTypeLabel: string;
      detailPurchaseDateLabel: string;
      detailWarrantyEndsLabel: string;
      detailNotesLabel: string;
      detailDocumentLabel: string;
      detailDocumentPresent: string;
      detailDocumentMissing: string;
      detailNoAssetType: string;
      detailNoPurchaseDate: string;
      detailNoWarrantyEnds: string;
      detailNoNotes: string;
      detailEditCta: string;
      detailWarrantyExpired: string;
      detailWarrantyActiveDays: string;
      /** Crear / editar ítem de bóveda. */
      createScreenTitle: string;
      editScreenTitle: string;
      saveSuccess: string;
      editSuccess: string;
      saveError: string;
      deleteCta: string;
      deleteConfirm: string;
      deleteSuccess: string;
      deleteError: string;
      nameLabel: string;
      namePlaceholder: string;
      assetTypeLabel: string;
      assetTypePlaceholder: string;
      purchaseDateLabel: string;
      warrantyEndsLabel: string;
      notesLabel: string;
      notesPlaceholder: string;
      saveCta: string;
      saving: string;
      /** Listado bóveda (maqueta editorial). */
      heroEyebrow: string;
      heroTitle: string;
      heroDescription: string;
      criticalEyebrow: string;
      criticalBodyUrgent: string;
      criticalBodyExpired: string;
      criticalCtaDetails: string;
      criticalCtaExtend: string;
      statTotalLabel: string;
      statDocsLabel: string;
      filterAll: string;
      sectionCount: string;
      cardPurchaseLabel: string;
      cardExpiresLabel: string;
      viewReceiptCta: string;
      registerNewCta: string;
      badgeExpired: string;
      badgeEndsToday: string;
      badgeDaysLeft: string;
      badgeMonthsLeft: string;
      badgeYearsLeft: string;
      badgeSafe: string;
      badgeNoDate: string;
      formEyebrow: string;
      formTitle: string;
      formSubtitle: string;
      uploadSectionLabel: string;
      uploadTitle: string;
      uploadSubtitle: string;
      /** Tipos MIME y tamaño máx. alineados con la API de almacenamiento. */
      uploadTypesHint: string;
      /** CTA selector documentos (PDF e imágenes permitidas). */
      uploadPickFilesCta: string;
      /** CTA galería de fotos (tras permiso en Android). */
      uploadPickGalleryCta: string;
      uploadUploading: string;
      uploadAttachedHint: string;
      uploadRemoveCta: string;
      uploadRemoveA11y: string;
      uploadErrorMime: string;
      uploadErrorSize: string;
      uploadPermissionDenied: string;
      uploadErrorGeneric: string;
      uploadSuccessReady: string;
      /** @deprecated sustituido por subida real; mantener por compat. */
      uploadSoon: string;
      completionStatusLabel: string;
      saveDisclaimer: string;
      moreOptionsA11y: string;
    };
    /** Filtros del diario de gastos (Stitch `filtros_de_gastos`). */
    journalFilters: {
      screenTitle: string;
      badge: string;
      title: string;
      subtitle: string;
      categoriesSection: string;
      membersSection: string;
      dateRangeSection: string;
      paidStatusSection: string;
      scopeSection: string;
      fromLabel: string;
      toLabel: string;
      paidOnly: string;
      unpaidOnly: string;
      allStatuses: string;
      applyCta: string;
      resetCta: string;
      applying: string;
    };
    /** Perfil de usuario (Stitch `perfil_de_usuario`). */
    profile: {
      screenTitle: string;
      settingsItem: string;
      settingsItemSub: string;
      notificationsItem: string;
      notificationsItemSub: string;
      securityItem: string;
      securityItemSub: string;
      helpItem: string;
      helpItemSub: string;
      signOutItem: string;
      signOutItemSub: string;
      deleteAccountItem: string;
      deleteAccountItemSub: string;
      deleteAccountConfirmTitle: string;
      deleteAccountConfirmMessage: string;
      deleteAccountConfirmCta: string;
      deleteAccountError: string;
      totalAssetsLabel: string;
      activeBudgetsLabel: string;
      comingSoon: string;
    };
    /** Hub y subpantallas de cuenta / ajustes (paridad dashboard). */
    settings: {
      hubTitle: string;
      hubSubtitle: string;
      backToAccount: string;
      cardPersonalTitle: string;
      cardPersonalHint: string;
      cardPasswordTitle: string;
      cardPasswordHint: string;
      cardSessionsTitle: string;
      cardSessionsHint: string;
      cardPasskeysTitle: string;
      cardPasskeysHint: string;
      cardNotificationsTitle: string;
      cardNotificationsHint: string;
      cardSupportTitle: string;
      cardSupportHint: string;
      cardAppearanceTitle: string;
      cardAppearanceHint: string;
      avatarChangeA11y: string;
      avatarUploading: string;
      clearAvatar: string;
      avatarPickError: string;
      avatarUploadError: string;
      footerBrand: string;
      personalTitle: string;
      personalSubtitle: string;
      emailLabel: string;
      emailHint: string;
      nameField: string;
      profileFormSectionTitle: string;
      saveButton: string;
      saving: string;
      savedHint: string;
      appearanceTitle: string;
      appearanceSubtitle: string;
      appearanceLight: string;
      appearanceDark: string;
      appearanceSystem: string;
      appearanceCurrent: string;
      sessionsTitle: string;
      sessionsSubtitle: string;
      sessionsIntro: string;
      sessionsLoadError: string;
      sessionsEmpty: string;
      sessionsSignOutOthers: string;
      sessionsSignOutDevice: string;
      sessionsThisDevice: string;
      sessionsCurrentBadge: string;
      sessionsLastActive: string;
      sessionsStarted: string;
      sessionsExpires: string;
      sessionsIp: string;
      sessionsUnknownUa: string;
      sessionsSignOutDeviceConfirm: string;
      sessionsSignOutOthersConfirm: string;
      sessionsActionError: string;
      passkeysTitle: string;
      passkeysSubtitle: string;
      passkeysBiometricTitle: string;
      passkeysBiometricHint: string;
      passkeysBiometricSuccess: string;
      passkeysBiometricError: string;
      passkeysContextTitle: string;
      passkeysContextHint: string;
      passkeysContextSuccess: string;
      passkeysContextSuccessHint: string;
      passkeysContextError: string;
      notificationsTitle: string;
      notificationsSubtitle: string;
      pushSectionTitle: string;
      activatePush: string;
      pushWorking: string;
      pushConfigMissing: string;
      pushPermissionDenied: string;
      pushRegisterSuccess: string;
      pushFailureNoSession: string;
      pushFailurePermissionRequest: string;
      pushFailureIosRemote: string;
      pushFailureToken: string;
      pushRegisterError: string;
      prefsSectionTitle: string;
      prefPushMaster: string;
      prefHouseInvites: string;
      prefExpenses: string;
      prefBudget: string;
      prefReminders: string;
      prefScanDocs: string;
      prefDigests: string;
      prefUpcomingCharges: string;
      devicesSectionTitle: string;
      noPushDevices: string;
      lastSeenLabel: string;
      devicePushLabel: string;
      removeDevice: string;
      removeDeviceConfirm: string;
      platformWeb: string;
      platformIos: string;
      platformAndroid: string;
      supportTitle: string;
      supportSubtitle: string;
      supportIntro: string;
      supportForgotTitle: string;
      supportForgotBody: string;
      supportForgotCta: string;
    };
    scan: {
      screenTitle: string;
      subtitle: string;
      scanningHint: string;
      openGallery: string;
      manualEntry: string;
      recentTitle: string;
      empty: string;
      emptyTitle: string;
      emptyDescription: string;
      emptyCta: string;
      statusPending: string;
      statusProcessing: string;
      statusCompleted: string;
      statusFailed: string;
      uploading: string;
      registering: string;
      /** Toast tras encolar un escaneo correctamente. */
      uploadSuccess: string;
      pollHint: string;
      createExpenseFromScan: string;
      extractedTitle: string;
      extractedMerchant: string;
      extractedDate: string;
      extractedTotal: string;
      extractionErrorFallback: string;
      permissionDenied: string;
      permissionBlocked: string;
    };
    /** Formulario crear/editar gasto. */
    addExpense: {
      createTitle: string;
      editTitle: string;
      saveSuccess: string;
      editSuccess: string;
      saveError: string;
      invalidAmount: string;
      invalidCurrency: string;
      titleOrCategoryRequired: string;
      titleLabel: string;
      titlePlaceholder: string;
      amountLabel: string;
      dateLabel: string;
      categoryLabel: string;
      categoryNone: string;
      paymentMethodLabel: string;
      paymentMethodNone: string;
      paymentMethodCard: string;
      paymentMethodCash: string;
      paymentMethodAccount: string;
      paidByLabel: string;
      paidByDefault: string;
      paidNowLabel: string;
      notesLabel: string;
      notesPlaceholder: string;
      receiptLabel: string;
      receiptAttachCta: string;
      receiptAttachedHint: string;
      receiptClearA11y: string;
      receiptUploading: string;
      receiptUploadError: string;
      receiptLinkedExisting: string;
      isRecurringLabel: string;
      recurrenceKindLabel: string;
      recurrenceMonthlyFixed: string;
      recurrenceSeries: string;
      frequencyLabel: string;
      freqMonthly: string;
      freqBimonthly: string;
      freqQuarterly: string;
      freqSemiannual: string;
      freqAnnual: string;
      endsOnLabel: string;
      endsOnPlaceholder: string;
      proratedLabel: string;
      monthlyFixedHint: string;
      submitCreate: string;
      submitCreateRecurring: string;
      submitSave: string;
      /** Bloque resumen antes del envío (detalle + serie). */
      beforeSubmitSummaryTitle: string;
      summaryConcept: string;
      summaryUnnamed: string;
      summaryTotalAmount: string;
      summaryInstallmentAmount: string;
      summaryDate: string;
      summaryCategory: string;
      summaryPaymentMethod: string;
      summaryPaidBy: string;
      summarySharedSplit: string;
      summaryScheduleTitle: string;
      summaryMoreDates: string;
      enterAmountForSplit: string;
      sharedPreviewRangeInvalid: string;
      sharedPreviewFinalizeRecurrence: string;
      /** Lanzador y pasos del asistente (paridad con dashboardUi.expenses.flows). */
      flows: {
        launcherTitle: string;
        launcherSubtitle: string;
        manualOnlyBanner: string;
        intentQuickTitle: string;
        intentQuickDescription: string;
        intentRecurringTitle: string;
        intentRecurringDescription: string;
        intentSeriesTitle: string;
        intentSeriesDescription: string;
        quickExpandMore: string;
        repeatLastFlowCta: string;
        navNext: string;
        navBack: string;
        navReview: string;
        editStepA11y: string;
        monthlyWizard: {
          step1Title: string;
          step1Help: string;
          step2Title: string;
          step2Help: string;
          step3Title: string;
          step3Help: string;
          step4Title: string;
          step4Help: string;
          step5Title: string;
          step5Help: string;
          reviewTitle: string;
          reviewHelp: string;
        };
        seriesWizard: {
          step1Title: string;
          step1Help: string;
          step2Title: string;
          step2Help: string;
          step3Title: string;
          step3Help: string;
          step4Title: string;
          step4Help: string;
          step5Title: string;
          step5Help: string;
          reviewTitle: string;
          reviewHelp: string;
        };
      };
    };
    /** Bandeja de notificaciones. */
    notifications: {
      screenTitle: string;
      empty: string;
      emptyHint: string;
      markAllRead: string;
      /** Confirmación antes de marcar todas las notificaciones como leídas. */
      markAllReadConfirm: string;
      markAllReadSuccess: string;
      markReadError: string;
    };
    /** Invitaciones a hogares pendientes. */
    pendingInvitations: {
      screenTitle: string;
      subtitle: string;
      empty: string;
      emptyHint: string;
      inviterLine: string;
      expiresLabel: string;
      acceptCta: string;
      accepting: string;
      acceptSuccess: string;
      acceptError: string;
    };
    /** Financiaciones del hogar. */
    financings: {
      screenTitle: string;
      empty: string;
      emptyHint: string;
      detailScreenTitle: string;
      installmentLabel: string;
      termProgressLabel: string;
      contractStartLabel: string;
      endsOnLabel: string;
      remainingLabel: string;
      openSeriesCta: string;
      noSeries: string;
      aiSummaryTitle: string;
      deleteCta: string;
      deleteConfirm: string;
      deleteSuccess: string;
      deleteError: string;
      /** Etiqueta corta para pendiente estimado en ficha. */
      remainingEstimatedShort: string;
      addDocumentCta: string;
      removeDocumentA11y: string;
      docMaxReached: string;
      docUploadError: string;
      docTooLarge: string;
      docMimeNotAllowed: string;
    };
    demo: {
      title: string;
      itemLabel: string;
      listSubtitle: string;
      loadingMore: string;
      sheetTitle: string;
      sheetBody: string;
      sheetCloseHint: string;
      fetchError: string;
      needSession: string;
      emptyNotifications: string;
      notificationType: string;
    };
    auth: {
      screenTitle: string;
      signInTitle: string;
      signInSubtitle: string;
      registerTitle: string;
      registerSubtitle: string;
      registerScreenTitle: string;
      forgotScreenTitle: string;
      forgotBadge: string;
      forgotTitle: string;
      /** Primera línea del titular (Stitch: «Recover your»). */
      forgotTitleLead: string;
      /** Segunda línea en acento primary (Stitch: «financial access.»). */
      forgotTitleAccent: string;
      forgotSubtitle: string;
      forgotBody: string;
      /** Etiqueta del campo email en recuperación (Stitch: «Registered email»). */
      forgotEmailLabel: string;
      forgotPlaceholderEmail: string;
      /** Enlace fijo tipo Stitch «Contact support». */
      contactSupport: string;
      /** Mensaje al pulsar soporte desde la app (sin canal dedicado aún). */
      contactSupportHint: string;
      /** Marca decorativa inferior (Stitch recuperar contraseña). */
      forgotDecorLetters: string;
      forgotDecorBrand: string;
      forgotDecorVersion: string;
      subtitle: string;
      nameLabel: string;
      namePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      passwordLabel: string;
      passwordPlaceholder: string;
      confirmPasswordLabel: string;
      signInCta: string;
      registerCta: string;
      sendRecoveryCta: string;
      signingIn: string;
      registering: string;
      sendingRecovery: string;
      signOut: string;
      goToSignIn: string;
      goToRegister: string;
      goToForgot: string;
      backToSignIn: string;
      orContinueWith: string;
      orRegisterWithEmail: string;
      socialGoogle: string;
      /** Corto; preferir `signInWithApple` / `signUpWithApple` en botones (HIG Apple). */
      socialApple: string;
      /** Texto completo del botón Sign in with Apple (HIG). */
      signInWithApple: string;
      /** Texto completo del botón Sign up with Apple en registro (HIG). */
      signUpWithApple: string;
      signInWithPasskey: string;
      signUpWithPasskey: string;
      continuingWithGoogle: string;
      continuingWithApple: string;
      continuingWithPasskey: string;
      socialApplePending: string;
      socialPasskey: string;
      errorGoogleFailed: string;
      errorGoogleCancelled: string;
      errorGoogleConfigMissing: string;
      errorAppleFailed: string;
      errorAppleCancelled: string;
      errorAppleConfigMissing: string;
      errorPasskeyFailed: string;
      errorPasskeyUnavailable: string;
      biometricPromptTitle: string;
      socialUnavailable: string;
      termsAgree: string;
      termsLink: string;
      privacyLink: string;
      termsConnector: string;
      termsRequired: string;
      alreadyHaveAccount: string;
      newHere: string;
      sessionActive: string;
      forgotSentHint: string;
      signIn: string;
      errorInvalidCredentials: string;
      errorGeneric: string;
      errorPasswordMismatch: string;
      errorSignUpFailed: string;
      errorRecoveryFailed: string;
      registerCheckEmail: string;
      /** Cambiar contraseña — pantalla `cambiar_contrase_a` (Stitch). */
      changePasswordScreenTitle: string;
      changePasswordBadge: string;
      changePasswordTitle: string;
      changePasswordSubtitle: string;
      currentPasswordLabel: string;
      currentPasswordPlaceholder: string;
      newPasswordLabel: string;
      newPasswordPlaceholder: string;
      confirmNewPasswordLabel: string;
      changePasswordCta: string;
      changingPassword: string;
      changePasswordSuccess: string;
      changePasswordSuccessHint: string;
      errorChangePasswordFailed: string;
      errorChangePasswordWrong: string;
      passwordStrengthWeak: string;
      passwordStrengthFair: string;
      passwordStrengthGood: string;
      passwordStrengthStrong: string;
      passwordStrengthLabel: string;
      securityTipsTitle: string;
      securityTip1Title: string;
      securityTip1Body: string;
      securityTip2Title: string;
      securityTip2Body: string;
      changePasswordSignOutHint: string;
    };
  };
}

